
import { FloatingFeature } from "@models";
import { 
  CreatureAbility,
  UpgradableNumber,
  ConsumedResource,
  CreatureInstance,
  SummonInstance,
  TransformInstance,
  RollPlus,
  AbilityEffect,
} from "@dndModels";
import { CastingTime, DurationType } from "@dndModels/Types";
import { STANDARD_CASTING_TIMES } from "@dndModels/Constants";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

const char_util = DandDCharacterUtilities.getInstance();

/**
 * These are spells and other abilities.  
 * I wanted to call them Abilities to make them more generic,
 * because I want to use this for things that aren't spells
 * (like Battlemaster Maneuvers, Weapon Attacks, Sneak Attack, etc).
 * 
 * bonus is a string, but it's complicated, so I'm going to 
 * define how it works here.
 * To help me make sure I understand it myself, I'm going to start
 * with some examples:
 * 
 * Weapon(die, bonus): 1 * {die} + {STR} + {bonus}
 * Range(die, bonus): 1 * {die} + {DEX} + {bonus}
 * Finesse(die, bonus): 1 * {die} + Max({STR},{DEX}) + {bonus}
 * SecondWeapon(die, bonus): 1 * {die} + {bonus}
 * SneakAttack(): Ceil({ClassLevel}/2) * {d6} 
 * 
 * EldritchBlast(): {d10} + ({Features[EldritchInvocations]}.includes("Agonizing Blast") ? {CHA} : 0)
 * Hex(): {d6}
 * BurningHands(slotLevel): (2 + {slotLevel}) * {d6}
 * CureWounds(slotLevel): {slotLevel} * {d8} + {SPM}
 * Note: Multiplier on a die roll doesn't mean roll once 
 * and then multiply.  
 * It means roll that many of it and sum.
 * Spells will each need their own formula.
 * And some will need two formulae, like Ice Knife.
 * Also Versatile weapons will pass different die based on 
 * one and two handed strikes.
 * 
 */
export class MinionAbility extends FloatingFeature {
  saving_throw_ability_score = ""; // Ability Score saving throw the target(s) have to make
  effects: AbilityEffect[] = []; // Formula for how much damage/healing to do
  range = ""; // Self, Touch, or a number
  range_2 = ""; // For some there are multiple ranges.  It can be an AoE size, or sometimes something else.
  concentration = false;
  notes = "";
  duration: DurationType | "" = "";
  components: string[] = []; // VSM
  material_component = "";
  casting_time: CastingTime = "A"; // A, BA, RA, X minute(s), etc
  special_casting_time = "";

  consumed_resource: ConsumedResource = new ConsumedResource();
  // resource_consumed: string = ""; // Slot-X, Ki, Lay on Hands, Charge, etc.
  // amount_consumed: number = 0;
  // special_resource_amount: UpgradableNumber = new UpgradableNumber();
  // special_resource_refresh_rule: string = ""; // Short Rest, Long Rest, Dawn, 1 Hour, 8 Hours, 24 Hours

  attack_bonus: UpgradableNumber = new UpgradableNumber();
  dc: UpgradableNumber = new UpgradableNumber();
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.saving_throw_ability_score = obj.saving_throw_ability_score ? obj.saving_throw_ability_score : "";
      if (obj.effects && obj.effects.length) {
        obj.effects.forEach((effect: any) => {
          this.effects.push(new AbilityEffect(effect));
        });
      } else if (obj.effect) {
        this.effects.push(new AbilityEffect(obj.effect));
      }
      this.range = obj.range ? obj.range : null;
      this.range_2 = obj.range_2 ? obj.range_2 : null;
      this.concentration = obj.concentration ? obj.concentration : false;
      this.notes = obj.notes ? obj.notes : "";
      this.duration = obj.duration ? obj.duration : "Instantaneous";
      this.components = obj.components ? [...obj.components] : [];
      this.material_component = obj.material_component ? obj.material_component : "";
      this.casting_time = obj.casting_time ? obj.casting_time : "A";
      
      this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
      if (!obj.consumed_resource) {
        this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "None";
        this.consumed_resource.amount_consumed = obj.amount_consumed ? +obj.amount_consumed : 0;
        if (obj.special_resource_amount && obj.special_resource_amount.base === undefined) {
          // Translate old set up to new
          this.consumed_resource.special_resource_amount = new UpgradableNumber();
          this.consumed_resource.special_resource_amount.base = obj.special_resource_amount;
        } else if (obj.special_resource_amount) {
          this.consumed_resource.special_resource_amount = new UpgradableNumber(obj.special_resource_amount);
        } else {
          this.consumed_resource.special_resource_amount = new UpgradableNumber();
        }
        this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
      }
      
      this.attack_bonus = obj.attack_bonus ? new UpgradableNumber(obj.attack_bonus) : new UpgradableNumber();
      this.dc = obj.dc ? new UpgradableNumber(obj.dc) : new UpgradableNumber();
      this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
      if (!STANDARD_CASTING_TIMES.includes(this.casting_time)) {
        this.special_casting_time = this.casting_time;
        this.casting_time = "Special";
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.saving_throw_ability_score = obj.saving_throw_ability_score ? obj.saving_throw_ability_score : "";
  //     if (obj.effects && obj.effects.length) {
  //       obj.effects.forEach((effect: any) => {
  //         this.effects.push(new AbilityEffect(effect));
  //       });
  //     } else if (obj.effect) {
  //       this.effects.push(new AbilityEffect(obj.effect));
  //     }
  //     this.range = obj.range ? obj.range : null;
  //     this.range_2 = obj.range_2 ? obj.range_2 : null;
  //     this.concentration = obj.concentration ? obj.concentration : false;
  //     this.notes = obj.notes ? obj.notes : "";
  //     this.duration = obj.duration ? obj.duration : "Instantaneous";
  //     this.components = obj.components ? [...obj.components] : [];
  //     this.material_component = obj.material_component ? obj.material_component : "";
  //     this.casting_time = obj.casting_time ? obj.casting_time : "A";
      
  //     this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
  //     if (!obj.consumed_resource) {
  //       this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "None";
  //       this.consumed_resource.amount_consumed = obj.amount_consumed ? +obj.amount_consumed : 0;
  //       if (obj.special_resource_amount && obj.special_resource_amount.base === undefined) {
  //         // Translate old set up to new
  //         this.consumed_resource.special_resource_amount = new UpgradableNumber();
  //         this.consumed_resource.special_resource_amount.base = obj.special_resource_amount;
  //       } else if (obj.special_resource_amount) {
  //         this.consumed_resource.special_resource_amount = new UpgradableNumber(obj.special_resource_amount);
  //       } else {
  //         this.consumed_resource.special_resource_amount = new UpgradableNumber();
  //       }
  //       this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
  //     }
      
  //     this.attack_bonus = obj.attack_bonus ? new UpgradableNumber(obj.attack_bonus) : new UpgradableNumber();
  //     this.dc = obj.dc ? new UpgradableNumber(obj.dc) : new UpgradableNumber();
  //     this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
  //     if (!STANDARD_CASTING_TIMES.includes(this.casting_time)) {
  //       this.special_casting_time = this.casting_time;
  //       this.casting_time = "Special";
  //     }
  //   }
  // }
  
  get the_effect_types() {
    return this.effects.map(o => o.type);
  }
  
  get damage_types() {
    return this.effects.flatMap(o => o.damage_types);
  }

  get the_attack_types() {
    return this.effects.map(o => o.attack_type);
  }

  get the_utility_details(): string[] {
    return this.effects.filter(o => ["Control","Utility"].includes(o.type)).map(o => o.utility_detail);
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      saving_throw_ability_score: this.saving_throw_ability_score,
      effects: this.effects.map(o => o.toDBObj()),
      range: this.range,
      range_2: this.range_2,
      concentration: this.concentration,
      notes: this.notes,
      duration: this.duration,
      components: this.components,
      material_component: this.material_component,
      casting_time: this.casting_time,
      special_casting_time: this.special_casting_time,
      consumed_resource: this.consumed_resource.toDBObj(),
      // resource_consumed: this.resource_consumed,
      // amount_consumed: this.amount_consumed,
      // special_resource_amount: this.special_resource_amount.toDBObj(),
      // special_resource_refresh_rule: this.special_resource_refresh_rule,
      attack_bonus: this.attack_bonus.toDBObj(),
      dc: this.dc.toDBObj()
    };
  }

  get effect(): AbilityEffect {
    if (this.effects.length > 0) {
      return this.effects[0];
    } else {
      return new AbilityEffect();
    }
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof MinionAbility) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: MinionAbility) {
    this.saving_throw_ability_score = copyMe.saving_throw_ability_score;
    this.effects = [];
    copyMe.effects.forEach(effect => {
      this.effects.push(effect.clone());
    });
    this.range = copyMe.range;
    this.range_2 = copyMe.range_2;
    this.concentration = copyMe.concentration;
    this.notes = copyMe.notes;
    this.duration = copyMe.duration;
    this.components = [...copyMe.components];
    this.material_component = copyMe.material_component;
    this.casting_time = copyMe.casting_time;
    this.special_casting_time = copyMe.special_casting_time;
    this.consumed_resource = copyMe.consumed_resource.clone();
    // this.resource_consumed = copyMe.resource_consumed;
    // this.amount_consumed = copyMe.amount_consumed;
    // this.special_resource_amount = copyMe.special_resource_amount.clone();
    // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    this.attack_bonus = copyMe.attack_bonus.clone();
    this.dc = copyMe.dc.clone();
  }

  copyCreatureAbility(copyMe: CreatureAbility): void {
    super.copy(copyMe, true);
    this.feature_type = "Minion Ability";
    this.saving_throw_ability_score = copyMe.saving_throw_ability_score;
    this.effects = [];
    copyMe.effects.forEach(effect => {
      const new_effect = new AbilityEffect();
      new_effect.copy(effect);
      this.effects.push(new_effect);
    });
    this.range = copyMe.range;
    this.range_2 = copyMe.range_2;
    this.concentration = copyMe.concentration;
    this.notes = copyMe.notes;
    this.duration = copyMe.duration;
    this.components = [...copyMe.components];
    this.material_component = copyMe.material_component;
    this.casting_time = copyMe.casting_time;
    this.consumed_resource = copyMe.consumed_resource.clone();
    // this.resource_consumed = copyMe.resource_consumed;
    // this.amount_consumed = copyMe.amount_consumed;
    // this.special_resource_amount = new UpgradableNumber();
    // this.special_resource_amount.base = copyMe.special_resource_amount;
    // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    this.attack_bonus = copyMe.attack_bonus.clone();
    this.dc = copyMe.dc.clone();
  }

  clone(drop_ids = false): MinionAbility {
    const new_obj = new MinionAbility();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
    
  get_potence_string(
    obj: CreatureInstance | SummonInstance | TransformInstance,
    effect: AbilityEffect, 
    log_it = false): string {
    let damage_string = "";
    const rolls: RollPlus[] = [];
    for (const potence of effect.potences) {
      // damage_string += potence.rolls. + " ";
      const roll = new RollPlus();
      roll.count = char_util.value(potence.rolls.count, { obj }); // , log_it });
      roll.size = char_util.value(potence.rolls.size, { obj }); // , log_it });
      roll.flat = char_util.value(potence.rolls.flat, { obj, log_it });
      roll.type = effect.type === "Damage" && effect.damage_types.length === 1 ? effect.damage_types[0] : effect.type;
      if (log_it) {
        console.log(damage_string);
        console.log(roll);
        // debugger;
      }
      roll.recalculate_string();
      damage_string += roll.as_string;
      if (log_it) {
        console.log(damage_string);
        // debugger;
      }
      rolls.push(roll);
    }
    const bonus = effect.bonus.base; // For Creatures, this is the only bonus it can have
    if (bonus !== 0) {
      damage_string += `${bonus} `;
    }
    // damage_string += effect.type === "Damage" && effect.damage_types.length === 1 ? `{${effect.damage_types[0]}}` : `{${effect.type}} `;
    return damage_string;
  }
}